import { useState, useCallback, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "react-router-dom";
import { z } from "zod";

export interface OrderItem {
  title: string;
  variantTitle?: string | null;
  quantity: number;
  sku?: string;
  price?: string;
  imageUrl?: string | null;
}

export interface OrderFulfillment {
  status: string;
  createdAt: string;
  updatedAt?: string;
  trackingInfo: Array<{ number: string; url?: string; company?: string }>;
  items: Array<{ title: string; quantity: number }>;
}

export interface OrderTimeline {
  status: string;
  date: string | null;
  completed: boolean;
}

export interface DetailedOrder {
  id: string;
  orderNumber: string;
  createdAt: string;
  financialStatus: string;
  fulfillmentStatus: string;
  subtotal: string;
  shipping: string;
  tax: string;
  total: string;
  currencyCode: string;
  note?: string;
  shippingAddress?: {
    name: string;
    address1?: string;
    address2?: string;
    city?: string;
    zip?: string;
    country?: string;
  } | null;
  items: OrderItem[];
  fulfillments: OrderFulfillment[];
  timeline: OrderTimeline[];
  paymentMethod?: string | null;
}

export interface Order {
  orderNumber: string;
  createdAt: string;
  status: string;
  paymentStatus: string;
  total: string;
  tracking?: { number: string; url?: string } | null;
  items: string;
}

export interface Repair {
  id: string;
  order_number?: number;
  orderNumber?: number;
  title: string;
  description?: string;
  status: string;
  statusLabel?: string;
  created_at?: string;
  createdAt?: string;
  updatedAt?: string;
  timeline?: Array<{
    status: string;
    date: string | null;
    completed: boolean;
  }>;
}

export interface TryOnResult {
  resultImage: string;
  products: Array<{
    handle: string;
    title: string;
    price: string;
    image: string;
  }>;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  type: "text" | "options" | "products" | "request_order_number" | "request_phone" | "otp_sent" | "request_otp" | "order_detail" | "redirect_contact" | "redirect_track_order" | "redirect_profile_repairs" | "open_whatsapp" | "error" | "main_menu" | "contact_welcome";
  options?: Array<{ label: string; value: string; emoji?: string }>;
  products?: Array<{
    id: string;
    title: string;
    handle: string;
    price: string;
    currency: string;
    image: string | null;
    variantId: string;
    availableForSale: boolean;
    variants?: Array<{
      id: string;
      title: string;
      price: string;
      currency: string;
      availableForSale: boolean;
      selectedOptions?: Array<{ name: string; value: string }>;
      image?: string | null;
    }>;
    options?: Array<{
      name: string;
      values: string[];
    }>;
    selectedVariantIndex?: number;
  }>;
  preferredVariant?: string;
  orders?: Order[];
  repairs?: Repair[];
  detailedOrder?: DetailedOrder;
  detailedRepair?: Repair;
  phone?: string;
  whatsappUrl?: string;
  tryOnResult?: TryOnResult;
  timestamp: Date;
}

interface OrderTrackingContext {
  orderNumber?: string;
  phone?: string;
  awaitingPhone?: boolean;
  awaitingOtp?: boolean;
}

const INITIAL_MESSAGE: ChatMessage = {
  id: "welcome",
  role: "assistant",
  content: "שלום! אני העוזר הדיגיטלי של Alice. במה אוכל לעזור?",
  type: "main_menu",
  options: [
    { label: "מצא מוצר", value: "__find_product__", emoji: "💎" },
    { label: "צור קשר באתר", value: "__contact_site__", emoji: "📧" },
    { label: "צור קשר בוואטסאפ", value: "__contact_whatsapp__", emoji: "💬" },
    { label: "מעקב תיקון", value: "__track_order__", emoji: "🔧" },
  ],
  timestamp: new Date(),
};

const STORAGE_KEY = "jewelry-advisor-chat-history";
const CONTEXT_STORAGE_KEY = "jewelry-advisor-tracking-context";

// Helper to serialize messages for localStorage
const serializeMessages = (messages: ChatMessage[]): string => {
  return JSON.stringify(messages.map(msg => ({
    ...msg,
    timestamp: msg.timestamp.toISOString(),
  })));
};

// Helper to deserialize messages from localStorage
const deserializeMessages = (json: string): ChatMessage[] => {
  try {
    const parsed = JSON.parse(json);
    return parsed.map((msg: ChatMessage & { timestamp: string }) => ({
      ...msg,
      timestamp: new Date(msg.timestamp),
    }));
  } catch {
    return [INITIAL_MESSAGE];
  }
};

// Load initial messages from localStorage
const loadStoredMessages = (): ChatMessage[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const messages = deserializeMessages(stored);
      if (messages.length > 0) {
        // Force refresh stored welcome message to match current menu
        const welcomeMsg = messages.find(m => m.id === "welcome" && m.type === "main_menu");
        if (welcomeMsg && welcomeMsg.options) {
          const currentOptionValues = INITIAL_MESSAGE.options?.map(o => o.value) || [];
          const storedOptionValues = welcomeMsg.options.map(o => o.value);
          const menuChanged = currentOptionValues.length !== storedOptionValues.length || 
            currentOptionValues.some(v => !storedOptionValues.includes(v));
          if (menuChanged) {
            return messages.map(m => m.id === "welcome" ? INITIAL_MESSAGE : m);
          }
        }
        return messages;
      }
    }
  } catch (e) {
    console.error("Error loading chat history from localStorage:", e);
  }
  return [INITIAL_MESSAGE];
};

// Load tracking context from localStorage
const loadStoredContext = (): OrderTrackingContext => {
  try {
    const stored = localStorage.getItem(CONTEXT_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error("Error loading tracking context from localStorage:", e);
  }
  return {};
};

export function useJewelryAdvisor() {
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadStoredMessages());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderTrackingContext, setOrderTrackingContext] = useState<OrderTrackingContext>(() => loadStoredContext());
  const location = useLocation();

  // Save messages to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, serializeMessages(messages));
    } catch (e) {
      console.error("Error saving chat history to localStorage:", e);
    }
  }, [messages]);

  // Save tracking context to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CONTEXT_STORAGE_KEY, JSON.stringify(orderTrackingContext));
    } catch (e) {
      console.error("Error saving tracking context to localStorage:", e);
    }
  }, [orderTrackingContext]);

  // Show welcome message when navigating to contact page
  useEffect(() => {
    if (location.pathname === '/contact') {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.type === 'redirect_contact') {
        const welcomeMessage: ChatMessage = {
          id: `contact-welcome-${Date.now()}`,
          role: "assistant",
          content: "ברוכים הבאים לעמוד יצירת קשר! 📧\n\nכאן תוכלו לשלוח לנו הודעה ונחזור אליכם בהקדם.",
          type: "contact_welcome",
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, welcomeMessage]);
      }
    }
  }, [location.pathname]);

  const sendMessage = useCallback(
    async (content: string, isFromQuickReply = false) => {
      const normalized = content.trim();

      const isListRequest = (() => {
        const t = normalized.replace(/\s+/g, " ");
        return /(?:^|\s)(?:רשימת\s*(?:תיקונים|הזמנות)|כל\s+(?:התיקונים|ההזמנות)(?:\s+שלי)?|(?:התיקונים|ההזמנות)\s+שלי|ת(?:ן|ראה)\s+לי\s+את\s+כל)(?:\s|$)/.test(t);
      })();

      const buildNoListAssistantMessage = (): ChatMessage => ({
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content:
          "אני לא יכול להציג רשימת תיקונים/הזמנות בצ׳אט.\n\n" +
          "כדי לקבל מידע על תיקון ספציפי: תן לי *מספר תיקון*, ואז אאמת טלפון עם קוד (OTP) ואציג מידע על אותו תיקון בלבד.\n\n" +
          "אם תרצה לראות את כל התיקונים שלך בצורה מסודרת, אוכל להפנות אותך:",
        type: "options",
        options: [
          { label: "לעמוד מעקב תיקון", value: "__go_track_order_page__", emoji: "🧾" },
          { label: "לאזור האישי", value: "__go_profile_repairs__", emoji: "👤" },
        ],
        timestamp: new Date(),
      });

      // Handle special menu actions
      if (content === "__find_product__") {
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: "מעולה! בוא נמצא את התכשיט המושלם עבורך 💎\n\nלמי את/ה מחפש/ת?",
          type: "options",
          options: [
            { label: "לעצמי", value: "אני מחפש תכשיט לעצמי", emoji: "👤" },
            { label: "מתנה", value: "אני מחפש מתנה למישהו", emoji: "🎁" },
          ],
          timestamp: new Date(),
        };
        setMessages((prev) => [
          ...prev,
          { id: `user-${Date.now()}`, role: "user", content: "מצא מוצר", type: "text", timestamp: new Date() },
          assistantMessage,
        ]);
        return;
      }

      if (content === "__contact_site__") {
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: "מעביר אותך לעמוד יצירת קשר...",
          type: "redirect_contact",
          timestamp: new Date(),
        };
        setMessages((prev) => [
          ...prev,
          { id: `user-${Date.now()}`, role: "user", content: "צור קשר באתר", type: "text", timestamp: new Date() },
          assistantMessage,
        ]);
        return;
      }

      if (content === "__contact_whatsapp__") {
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: "פותח וואטסאפ...",
          type: "open_whatsapp",
          whatsappUrl: "https://wa.me/972545267564",
          timestamp: new Date(),
        };
        setMessages((prev) => [
          ...prev,
          { id: `user-${Date.now()}`, role: "user", content: "צור קשר בוואטסאפ", type: "text", timestamp: new Date() },
          assistantMessage,
        ]);
        return;
      }

      if (content === "__go_track_order_page__") {
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: "מעביר אותך לעמוד מעקב תיקון...",
          type: "redirect_track_order",
          timestamp: new Date(),
        };
        setMessages((prev) => [
          ...prev,
          { id: `user-${Date.now()}`, role: "user", content: "לעמוד מעקב תיקון", type: "text", timestamp: new Date() },
          assistantMessage,
        ]);
        return;
      }

      if (content === "__go_profile_repairs__") {
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: "מעביר אותך לאזור האישי...",
          type: "redirect_profile_repairs",
          timestamp: new Date(),
        };
        setMessages((prev) => [
          ...prev,
          { id: `user-${Date.now()}`, role: "user", content: "לאזור האישי", type: "text", timestamp: new Date() },
          assistantMessage,
        ]);
        return;
      }



      if (content === "__track_order__") {
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: "כדי לעקוב אחרי התיקון שלך, נא להזין את מספר התיקון:",
          type: "request_order_number",
          timestamp: new Date(),
        };
        setMessages((prev) => [
          ...prev,
          { id: `user-${Date.now()}`, role: "user", content: "מעקב תיקון", type: "text", timestamp: new Date() },
          assistantMessage,
        ]);
        return;
      }

      // Step 1: User provides repair number -> ask for phone
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.type === "request_order_number") {
        const userMessage: ChatMessage = {
          id: `user-${Date.now()}`,
          role: "user",
          content: normalized,
          type: "text",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMessage]);

        if (isListRequest) {
          setMessages((prev) => [...prev, buildNoListAssistantMessage()]);
          return;
        }

        const orderNumberSchema = z
          .string()
          .trim()
          .transform((v) => v.replace(/^#/, ""))
          .refine((v) => /^\d{1,10}$/.test(v), { message: "מספר תיקון לא תקין" });

        const parsed = orderNumberSchema.safeParse(normalized);
        if (!parsed.success) {
          const errorMsg: ChatMessage = {
            id: `error-${Date.now()}`,
            role: "assistant",
            content: parsed.error.issues[0]?.message || "מספר תיקון לא תקין",
            type: "error",
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMsg]);
          return;
        }

        const cleanOrderNumber = parsed.data;

        // Store order number and ask for phone
        setOrderTrackingContext({ orderNumber: cleanOrderNumber, awaitingPhone: true });

        const phoneRequestMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: "כדי לאמת ולשמור על פרטיות, נא להזין את מספר הטלפון המשויך לתיקון:",
          type: "request_phone",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, phoneRequestMessage]);
        return;
      }

      // Step 2: User provides phone -> send OTP
      if (lastMessage?.type === "request_phone" && orderTrackingContext.awaitingPhone) {
        const userMessage: ChatMessage = {
          id: `user-${Date.now()}`,
          role: "user",
          content: normalized,
          type: "text",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMessage]);

        if (isListRequest) {
          setMessages((prev) => [...prev, buildNoListAssistantMessage()]);
          return;
        }

        setIsLoading(true);

        // Validate phone
        const phoneSchema = z
          .string()
          .trim()
          .transform((v) => v.replace(/[-\s]/g, ""))
          .refine((v) => /^(05\d{8}|\+9725\d{8})$/.test(v), { message: "מספר טלפון לא תקין" });

        const parsed = phoneSchema.safeParse(normalized);
        if (!parsed.success) {
          const errorMsg: ChatMessage = {
            id: `error-${Date.now()}`,
            role: "assistant",
            content: parsed.error.issues[0]?.message || "מספר טלפון לא תקין",
            type: "error",
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMsg]);
          setIsLoading(false);
          return;
        }

        const cleanPhone = parsed.data;

        try {
          // Send OTP
          const { data, error: fnError } = await supabase.functions.invoke("send-otp", {
            body: { phone: cleanPhone },
          });

          if (fnError || data?.error) {
            throw new Error(data?.error || fnError?.message || "שגיאה בשליחת קוד");
          }

          // Update context and ask for OTP
          setOrderTrackingContext((prev) => ({
            ...prev,
            phone: cleanPhone,
            awaitingPhone: false,
            awaitingOtp: true,
          }));

          const masked = cleanPhone.slice(-4).padStart(cleanPhone.length, "*");

          const otpMessage: ChatMessage = {
            id: `assistant-${Date.now()}`,
            role: "assistant",
            content:
              `שלחנו עכשיו בקשה לקוד אימות למספר ${masked}.\n` +
              `אם לא הגיע תוך דקה, כתוב "שלח שוב".\n` +
              `נא להזין את הקוד בן 6 הספרות:`,
            type: "request_otp",
            phone: cleanPhone,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, otpMessage]);
        } catch (err) {
          console.error("Error sending OTP:", err);
          const errorMsg: ChatMessage = {
            id: `error-${Date.now()}`,
            role: "assistant",
            content: err instanceof Error ? err.message : "שגיאה בשליחת קוד האימות",
            type: "error",
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMsg, buildNoListAssistantMessage()]);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // Step 3: User provides OTP -> verify and fetch repair
      if (lastMessage?.type === "request_otp" && orderTrackingContext.awaitingOtp) {
        const userMessage: ChatMessage = {
          id: `user-${Date.now()}`,
          role: "user",
          content: normalized,
          type: "text",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMessage]);

        if (isListRequest) {
          setMessages((prev) => [...prev, buildNoListAssistantMessage()]);
          return;
        }

        const isResendRequest = /^(?:שלח|תשלח)\s*(?:שוב|מחדש|קוד\s*חדש)/.test(normalized);
        if (isResendRequest) {
          if (!orderTrackingContext.phone) {
            const errorMsg: ChatMessage = {
              id: `error-${Date.now()}`,
              role: "assistant",
              content: "חסר לי מספר טלפון לאימות. נתחיל מחדש — לחץ על 'מעקב תיקון'.",
              type: "error",
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMsg]);
            setOrderTrackingContext({});
            return;
          }

          setIsLoading(true);
          try {
            const { data, error: fnError } = await supabase.functions.invoke("send-otp", {
              body: { phone: orderTrackingContext.phone },
            });

            if (fnError || data?.error) {
              throw new Error(data?.error || fnError?.message || "שגיאה בשליחת קוד");
            }

            const masked = orderTrackingContext.phone.slice(-4).padStart(orderTrackingContext.phone.length, "*");
            const resendMsg: ChatMessage = {
              id: `assistant-${Date.now()}`,
              role: "assistant",
              content: `שלחתי שוב בקשה לקוד אימות למספר ${masked}. נא להזין את הקוד בן 6 הספרות:`,
              type: "request_otp",
              phone: orderTrackingContext.phone,
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, resendMsg]);
          } catch (err) {
            const errorMsg: ChatMessage = {
              id: `error-${Date.now()}`,
              role: "assistant",
              content: err instanceof Error ? err.message : "שגיאה בשליחת קוד האימות",
              type: "error",
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMsg]);
          } finally {
            setIsLoading(false);
          }

          return;
        }

        // Validate OTP format
        const otpSchema = z
          .string()
          .trim()
          .refine((v) => /^\d{6}$/.test(v), { message: "קוד אימות חייב להכיל 6 ספרות" });

        const parsed = otpSchema.safeParse(normalized);
        if (!parsed.success) {
          const errorMsg: ChatMessage = {
            id: `error-${Date.now()}`,
            role: "assistant",
            content: parsed.error.issues[0]?.message || "קוד אימות לא תקין",
            type: "error",
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMsg]);
          return;
        }

        const otpCode = parsed.data;

        setIsLoading(true);
        try {
          // Verify OTP and get repair
          const { data, error: fnError } = await supabase.functions.invoke("get-order-by-number", {
            body: {
              orderNumber: orderTrackingContext.orderNumber,
              phone: orderTrackingContext.phone,
              otpCode,
            },
          });

          if (fnError) throw new Error(fnError.message);

          // Clear context
          setOrderTrackingContext({});

          if (data?.error) {
            const errorMsg: ChatMessage = {
              id: `error-${Date.now()}`,
              role: "assistant",
              content: data.error,
              type: "error",
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMsg]);
          } else if (!data?.found) {
            const errorMsg: ChatMessage = {
              id: `error-${Date.now()}`,
              role: "assistant",
              content: "לא נמצא תיקון עם מספר זה המשויך לטלפון שהזנת",
              type: "error",
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, errorMsg]);
          } else {
            const repairMessage: ChatMessage = {
              id: `assistant-${Date.now()}`,
              role: "assistant",
              content: `נמצא תיקון #${data.repair.orderNumber || data.repair.order_number}`,
              type: "order_detail",
              detailedRepair: data.repair,
              timestamp: new Date(),
            };
            setMessages((prev) => [...prev, repairMessage]);
          }
        } catch (err) {
          console.error("Error verifying OTP:", err);
          const errorMsg: ChatMessage = {
            id: `error-${Date.now()}`,
            role: "assistant",
            content: "שגיאה באימות הקוד, נסה שוב",
            type: "error",
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, errorMsg]);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // If user asks for a full list, refuse and offer redirect.
      if (isListRequest) {
        const userMessage: ChatMessage = {
          id: `user-${Date.now()}`,
          role: "user",
          content: normalized,
          type: "text",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMessage, buildNoListAssistantMessage()]);
        return;
      }

      // Normal message flow
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: normalized,
        type: "text",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      try {
        // Build conversation history for the API
        const conversationHistory = [...messages, userMessage]
          .filter((m) => m.id !== "welcome" || m.role === "assistant")
          .filter((m) => m.type !== "main_menu" && m.type !== "contact_welcome")
          .map((m) => ({
            role: m.role,
            content: m.content,
          }));

        const { data, error: fnError } = await supabase.functions.invoke("jewelry-advisor", {
          body: {
            messages: conversationHistory,
            isFromQuickReply,
            context: orderTrackingContext,
          },
        });

        if (fnError) {
          console.error("Function error:", fnError);
          throw new Error(fnError.message || "שגיאה בתקשורת עם השרת");
        }

        if (data.error) {
          throw new Error(data.message || "שגיאה לא צפויה");
        }

        // Create assistant message based on response type
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          content: data.message,
          type: data.type,
          options: data.options,
          products: data.products,
          preferredVariant: data.preferredVariant,
          orders: data.orders,
          repairs: data.repairs,
          phone: data.phone,
          whatsappUrl: data.whatsappUrl,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, assistantMessage]);
      } catch (err) {
        console.error("Error in jewelry advisor:", err);
        setError(err instanceof Error ? err.message : "שגיאה לא צפויה");

        // Add error message to chat
        const errorMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          role: "assistant",
          content: "מצטער, נתקלתי בבעיה. נסה שוב בבקשה 🙏",
          type: "text",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      } finally {
        setIsLoading(false);
      }
    },
    [messages, orderTrackingContext]
  );

  const resetConversation = useCallback(() => {
    setMessages([INITIAL_MESSAGE]);
    setError(null);
    setOrderTrackingContext({});
    // Clear localStorage when resetting
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(CONTEXT_STORAGE_KEY);
    } catch (e) {
      console.error("Error clearing chat history from localStorage:", e);
    }
  }, []);

  const showContactWelcome = useCallback(() => {
    const welcomeMessage: ChatMessage = {
      id: `contact-welcome-${Date.now()}`,
      role: "assistant",
      content: "ברוכים הבאים לעמוד יצירת קשר! 📧\n\nכאן תוכלו לשלוח לנו הודעה ונחזור אליכם בהקדם.",
      type: "contact_welcome",
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, welcomeMessage]);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    resetConversation,
    showContactWelcome,
  };
}
